/**
 * Хук управления онбординг-туром по системе.
 * Туры адаптированы под роль пользователя (admin / grpi / service / pilot).
 * Завершённый тур сохраняется в localStorage и не показывается повторно.
 */
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuthStore } from '@/stores/authStore';

// ── Шаги туров по ролям ───────────────────────────────────────────────────

const TOURS = {
  grpi: [
    {
      element: '[data-tour="dashboard"]',
      popover: {
        title: 'Центр управления',
        description:
          'Главная панель с ключевыми показателями, статистикой полётов и событиями.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="flights"]',
      popover: {
        title: 'Все полёты',
        description:
          'Полный список полётов с фильтрацией по дате, борту, маршруту и пилоту.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="accounting-journal"]',
      popover: {
        title: 'Журнал смены',
        description:
          'Формируйте и печатайте журнал учёта обработки полётной информации за смену.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="search"]',
      popover: {
        title: 'Быстрый поиск',
        description:
          'Нажмите Ctrl+K чтобы найти любой борт, пилота, аэропорт или рейс.',
        side: 'bottom' as const,
      },
    },
  ],

  service: [
    {
      element: '[data-tour="safety-events"]',
      popover: {
        title: 'Мои события',
        description:
          'Список событий вашей службы. Просроченные дедлайны выделены красным.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="risk-map"]',
      popover: {
        title: 'Карта рисков',
        description:
          'Матрица оценки рисков вашей службы. Цвет ячейки — уровень приемлемости.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="search"]',
      popover: {
        title: 'Быстрый поиск',
        description: 'Нажмите Ctrl+K чтобы найти событие, документ или пользователя.',
        side: 'bottom' as const,
      },
    },
  ],

  admin: [
    {
      element: '[data-tour="dashboard"]',
      popover: {
        title: 'Центр управления',
        description:
          'Сводная статистика по всем службам. Нажмите на любую цифру чтобы перейти к деталям.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="sync"]',
      popover: {
        title: 'Синхронизация МДБ',
        description:
          'Управление синхронизацией с legacy-системой. Здесь видны ошибки импорта.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="users"]',
      popover: {
        title: 'Пользователи',
        description:
          'Управление доступом. Каждому пользователю можно назначить роль и службу.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="search"]',
      popover: {
        title: 'Быстрый поиск',
        description: 'Нажмите Ctrl+K чтобы найти пользователя, рейс или событие.',
        side: 'bottom' as const,
      },
    },
  ],

  pilot: [
    {
      element: '[data-tour="pilot-workspace"]',
      popover: {
        title: 'Личный кабинет',
        description: 'Ваша статистика — налёт, посадки, события в ваших полётах.',
        side: 'right' as const,
      },
    },
    {
      element: '[data-tour="my-flights"]',
      popover: {
        title: 'Мои полёты',
        description: 'Все ваши полёты с параметрами и событиями.',
        side: 'right' as const,
      },
    },
  ],
} as const;

type TourRole = keyof typeof TOURS;

// ── Хук ──────────────────────────────────────────────────────────────────

export function useOnboardingTour() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const user = useAuthStore((s) => (s as any).user);

  function getNavRole(): TourRole {
    if (!user) return 'service';
    if (user.has_full_access) return 'admin';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (user.services?.some((s: any) => s.is_flight_data_service)) return 'grpi';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (user.roles?.some((r: any) => r.name === 'pilot')) return 'pilot';
    return 'service';
  }

  function storageKey(role: TourRole) {
    return `onboarding_done_${user?.id ?? 'anon'}_${role}`;
  }

  function startTour() {
    const role = getNavRole();
    const steps = [...TOURS[role]];

    const driverObj = driver({
      showProgress: true,
      progressText: '{{current}} из {{total}}',
      nextBtnText: 'Далее →',
      prevBtnText: '← Назад',
      doneBtnText: 'Готово',
      steps,
    });

    driverObj.drive();
    localStorage.setItem(storageKey(role), '1');
  }

  function shouldShowTour(): boolean {
    if (!user) return false;
    const role = getNavRole();
    return !localStorage.getItem(storageKey(role));
  }

  function resetTour() {
    if (!user) return;
    const role = getNavRole();
    localStorage.removeItem(storageKey(role));
  }

  return { startTour, shouldShowTour, resetTour };
}
